import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  Bot,
  CalendarClock,
  ChevronDown,
  ClipboardList,
  FileText,
  Filter,
  FormInput,
  Gauge,
  Inbox,
  LayoutDashboard,
  MessageSquare,
  MoreHorizontal,
  PanelRight,
  RefreshCcw,
  Search,
  Settings,
  Sparkles,
  Target,
  Users,
  Workflow
} from "lucide-react";
import { formQuestions, getLeadStats, leads } from "./data/leads.js";
import "./styles.css";

const statusClass = {
  Hot: "hot",
  Qualified: "qualified",
  New: "new",
  "Needs Nurture": "nurture",
  "Needs Review": "review"
};

const navItems = [
  ["Dashboard", LayoutDashboard],
  ["Conversations", MessageSquare],
  ["Opportunities", Target],
  ["Sites", PanelRight],
  ["Forms", FormInput],
  ["Submissions", Inbox],
  ["Automations", Workflow],
  ["Settings", Settings]
];

function formatTime(value) {
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}

function apiUrl(path) {
  return `${window.location.origin}${path}`;
}

function Sidebar() {
  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brandMark">LI</div>
        <div>
          <strong>Lead Intake</strong>
          <span>Demo CRM</span>
        </div>
      </div>
      <nav className="navList">
        {navItems.map(([label, Icon], index) => (
          <button className={index === 5 ? "navItem active" : "navItem"} key={label}>
            <Icon size={17} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebarStatus">
        <div className="statusDot" />
        <div>
          <strong>API live</strong>
          <span>Fake GHL simulator</span>
        </div>
      </div>
    </aside>
  );
}

function Header() {
  return (
    <header className="topbar">
      <div className="locationSelect">
        <span>Mota Demo / Brad</span>
        <ChevronDown size={16} />
      </div>
      <div className="topSearch">
        <Search size={16} />
        <input aria-label="Search leads" placeholder="Search leads, company, campaign..." />
      </div>
      <button className="iconButton" aria-label="More options">
        <MoreHorizontal size={18} />
      </button>
      <div className="avatar" aria-label="Demo user">
        LM
      </div>
    </header>
  );
}

function StatCard({ icon: Icon, label, value, note }) {
  return (
    <section className="statCard">
      <div className="statIcon">
        <Icon size={18} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{note}</small>
      </div>
    </section>
  );
}

function LeadTable({ filteredLeads, selectedId, onSelect }) {
  return (
    <section className="tablePanel">
      <div className="panelHeader">
        <div>
          <h2>Form submissions</h2>
          <p>Social Ads Qualification Form</p>
        </div>
        <button className="softButton">
          <Filter size={16} />
          Filter
        </button>
      </div>
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>Lead</th>
              <th>Source</th>
              <th>City</th>
              <th>Status</th>
              <th>Monthly budget</th>
              <th>Timeline</th>
              <th>Score</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((lead) => (
              <tr
                key={lead.id}
                className={selectedId === lead.id ? "selected" : ""}
                onClick={() => onSelect(lead)}
              >
                <td>
                  <div className="leadCell">
                    <strong>{lead.name}</strong>
                    <span>{lead.company}</span>
                  </div>
                </td>
                <td>{lead.source}</td>
                <td>{lead.answers.city}</td>
                <td>
                  <span className={`statusChip ${statusClass[lead.status]}`}>{lead.status}</span>
                </td>
                <td>{lead.answers.monthly_budget}</td>
                <td>{lead.answers.timeline_to_start}</td>
                <td>
                  <span className="scorePill">{lead.score}</span>
                </td>
                <td>{formatTime(lead.submittedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function QuestionList({ lead }) {
  return (
    <div className="questionList">
      {formQuestions.map((question) => (
        <div className="questionItem" key={question.id}>
          <span>{question.shortLabel}</span>
          <p>{lead.answers[question.id]}</p>
        </div>
      ))}
    </div>
  );
}

function DetailPanel({ lead, onShowApi }) {
  return (
    <aside className="detailPanel">
      <div className="detailTop">
        <div>
          <span className="miniLabel">Selected lead</span>
          <h2>{lead.name}</h2>
          <p>{lead.company}</p>
        </div>
        <span className={`statusChip ${statusClass[lead.status]}`}>{lead.status}</span>
      </div>

      <div className="actionStack">
        <button className="primaryButton">
          <Sparkles size={16} />
          Generate pre-call analysis
        </button>
        <button className="softButton" onClick={onShowApi}>
          <FileText size={16} />
          View API payload
        </button>
      </div>

      <section className="analysisBox">
        <div className="analysisHeader">
          <Bot size={17} />
          <strong>Setter pre-call brief</strong>
        </div>
        <p>{lead.analysis.summary}</p>
        <div className="callout">
          <span>Suggested opener</span>
          <strong>{lead.analysis.opener}</strong>
        </div>
      </section>

      <section className="detailSection">
        <h3>Submitted form answers</h3>
        <QuestionList lead={lead} />
      </section>

      <section className="detailSection">
        <h3>Talking points</h3>
        <ul className="pointList">
          {lead.analysis.talkingPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>
    </aside>
  );
}

function ApiDrawer({ lead, isOpen, onClose }) {
  if (!isOpen) return null;

  const payload = {
    lead: apiUrl(`/api/leads/${lead.id}`),
    analysis: apiUrl(`/api/precall-analysis/${lead.id}`),
    formSubmissions: apiUrl("/api/forms/social-ads"),
    allLeads: apiUrl("/api/leads")
  };

  return (
    <div className="drawerScrim" role="presentation" onClick={onClose}>
      <section className="apiDrawer" role="dialog" aria-label="API payload" onClick={(event) => event.stopPropagation()}>
        <div className="drawerHeader">
          <div>
            <span className="miniLabel">Agent access</span>
            <h2>API payload</h2>
          </div>
          <button className="iconButton" onClick={onClose} aria-label="Close API payload">
            ×
          </button>
        </div>
        <pre>{JSON.stringify(payload, null, 2)}</pre>
        <div className="endpointList">
          <a href={payload.allLeads} target="_blank" rel="noreferrer">GET /api/leads</a>
          <a href={payload.lead} target="_blank" rel="noreferrer">GET /api/leads/{lead.id}</a>
          <a href={payload.analysis} target="_blank" rel="noreferrer">GET /api/precall-analysis/{lead.id}</a>
        </div>
      </section>
    </div>
  );
}

function App() {
  const [selectedLead, setSelectedLead] = useState(leads[0]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const stats = getLeadStats();

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const matchesStatus = statusFilter === "All" || lead.status === statusFilter;
      const haystack = [
        lead.name,
        lead.company,
        lead.source,
        lead.campaign,
        lead.answers.business_name,
        lead.answers.niche,
        lead.answers.city,
        lead.answers.website_link
      ]
        .join(" ")
        .toLowerCase();
      return matchesStatus && haystack.includes(query.toLowerCase());
    });
  }, [query, statusFilter]);

  return (
    <div className="appShell">
      <Sidebar />
      <main className="main">
        <Header />
        <section className="pageHeader">
          <div>
            <h1>Lead form intake</h1>
            <p>Fake GHL-style view of social ads leads, submitted answers, and setter-ready analysis.</p>
          </div>
          <div className="headerActions">
            <button className="softButton">
              <RefreshCcw size={16} />
              Sync latest leads
            </button>
            <button className="primaryButton">
              <Sparkles size={16} />
              Generate batch analysis
            </button>
          </div>
        </section>

        <section className="statsGrid">
          <StatCard icon={Users} label="Total submissions" value={stats.total} note="From social ad forms" />
          <StatCard icon={Gauge} label="Average score" value={`${stats.avgScore}/100`} note="Lead fit estimate" />
          <StatCard icon={Activity} label="Hot leads" value={stats.hot} note="Call immediately" />
          <StatCard icon={CalendarClock} label="Assigned" value={stats.assigned} note="Setter owner set" />
        </section>

        <section className="workbench">
          <div className="contentColumn">
            <section className="toolbar">
              <div className="segmented">
                {["All", "Hot", "Qualified", "New", "Needs Nurture"].map((status) => (
                  <button
                    key={status}
                    className={statusFilter === status ? "active" : ""}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
              <label className="localSearch">
                <Search size={16} />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search table" />
              </label>
            </section>
            <LeadTable filteredLeads={filteredLeads} selectedId={selectedLead.id} onSelect={setSelectedLead} />
            <section className="apiStrip">
              <ClipboardList size={18} />
              <div>
                <strong>Agent API surface</strong>
                <span>GET /api/leads · GET /api/leads/:id · GET /api/forms/social-ads · GET /api/precall-analysis/:id</span>
              </div>
            </section>
          </div>
          <DetailPanel lead={selectedLead} onShowApi={() => setDrawerOpen(true)} />
        </section>
      </main>
      <ApiDrawer lead={selectedLead} isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

createRoot(document.getElementById("root")).render(<App />);
